import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { pwaCacheManager } from '@/lib/pwaCache';

/**
 * PWA Update Notifier - PWABuilder Best Practices
 * Handles service worker updates and notifies users when new version is available
 * Follows PWABuilder recommendations for update handling
 */
export function PWAUpdateNotifier() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Don't attempt SW operations in preview/iframe environments
    const host = window.location.hostname;
    if (host.includes('id-preview--') || host.includes('lovableproject.com')) return;
    try { if (window.self !== window.top) return; } catch { return; }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);
        
        // Check for waiting service worker (update available)
        if (reg.waiting) {
          setUpdateAvailable(true);
        }

        // Listen for new service worker
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          }
        });

        // Listen for controller change (new SW activated)
        // Only reload if we've explicitly triggered an update
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (isUpdating) {
            console.log('[PWA] New service worker activated, reloading...');
            window.location.reload();
          }
        });
        
        // Listen for messages from service worker
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data?.type === 'CACHE_CLEARED') {
            console.log('[PWA] Cache cleared by service worker');
          }
        });
      });
    }
  }, [isUpdating]);

  const handleUpdate = async () => {
    setIsUpdating(true);
    
    if (registration?.waiting) {
      // Check if offline books exist
      const books = await pwaCacheManager.getOfflineBookList();
      const hasOfflineBooks = books.length > 0;
      const message = hasOfflineBooks 
        ? 'Updating app... Your downloaded books will remain available.'
        : 'Updating app...';
      
      toast(message, { duration: 3000 });
      
      // Tell waiting SW to activate
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      setUpdateAvailable(false);
      
      // The controllerchange listener will reload the page
    } else {
      // Fallback: force update check
      try {
        await registration?.update?.();
        toast('Checking for updates...', { duration: 2000 });
      } catch (error) {
        console.error('Update check failed:', error);
      }
      setIsUpdating(false);
    }
  };

  // Show toast notification when update is available
  useEffect(() => {
    if (updateAvailable) {
      toast(
        <div className="flex items-center justify-between gap-4">
          <span>App update available!</span>
          <Button size="sm" onClick={handleUpdate}>
            Update
          </Button>
        </div>,
        {
          duration: 10000,
          position: 'top-center',
        }
      );
    }
  }, [updateAvailable]);

  return null; // This component only shows toasts
}